import { useState } from "react";
import { motion } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import logoMark from "@/assets/nur-logo.png";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const lenis = useLenis(({ scroll }) => setScrolled(scroll > 60));

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -10, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const dark = scrolled || open;

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        dark
          ? "bg-white/85 backdrop-blur-xl border-b border-sage/30"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => go("hero")}
          aria-label="Nur Therapy — home"
          className="flex items-center"
        >
          <img
            src={logoMark}
            alt="Nur Therapy"
            className="h-11 w-auto transition-transform duration-500 hover:scale-105 md:h-14"
          />
        </button>

        <div className="hidden items-center gap-10 md:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`group relative text-sm tracking-wide transition-colors duration-300 ${
                dark ? "text-stone hover:text-forest" : "text-white/85 hover:text-white"
              }`}
            >
              {l.label}
              <span
                className={`absolute -bottom-1 left-0 h-px w-0 transition-all duration-300 group-hover:w-full ${
                  dark ? "bg-forest" : "bg-white"
                }`}
              />
            </button>
          ))}
          <button
            data-testid="nav-book-btn"
            onClick={() => go("contact")}
            className={`rounded-full border px-6 py-2.5 text-sm tracking-wide transition-all duration-300 ${
              dark
                ? "border-forest bg-forest text-cream hover:bg-transparent hover:text-forest"
                : "border-white/70 text-white hover:bg-white hover:text-forest"
            }`}
          >
            Book a Session
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden transition-colors ${dark ? "text-forest" : "text-white"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <motion.div
          data-testid="mobile-menu"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="overflow-hidden border-t border-sage/30 bg-white md:hidden"
        >
          <div className="flex flex-col px-6 py-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-link-${l.id}`}
                onClick={() => go(l.id)}
                className="border-b border-sage/20 py-4 text-left font-serif text-2xl text-forest"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-book-btn"
              onClick={() => go("contact")}
              className="mt-5 rounded-full bg-forest px-6 py-3 text-sm text-cream"
            >
              Book a Session
            </button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
